"use client";

import { useEffect, useState } from "react";
import { devLabel } from "@/lib/dev-labels";
import { helperPing } from "@/lib/helper-client";

export type HelperStatus = "connected" | "fallback" | "offline" | "checking";

const PRESETS: Record<
	HelperStatus,
	{ label: string; bg: string; color: string }
> = {
	connected: {
		label: "Helper 연결됨",
		bg: "rgba(34,197,94,0.12)",
		color: "#22c55e",
	},
	fallback: {
		label: "Cloud 폴백",
		bg: "rgba(212,175,55,0.12)",
		color: "#d4af37",
	},
	offline: {
		label: "오프라인",
		bg: "rgba(239,68,68,0.12)",
		color: "#ef4444",
	},
	checking: {
		label: "Helper 확인중",
		bg: "rgba(163,163,163,0.12)",
		color: "#a3a3a3",
	},
};

const CYCLE: HelperStatus[] = ["connected", "fallback", "offline"];

export interface HelperStatusDotProps {
	/** Initial status (default "checking"). After mount we auto-resolve. */
	initial?: HelperStatus;
	onStatusChange?: (s: HelperStatus) => void;
}

/**
 * HelperStatusDot — Phase 25-AB Task 18 #16 (panel 75 / 9-G).
 *
 * Pill in the Welcome header that shows Hybrid Helper status. Click to cycle
 * through demo states (connected → fallback → offline) so the user can preview
 * the corresponding fallback banner without actually disconnecting Helper.
 *
 * Mockup source: docs/mockups/welcome.html lines 310-314 + 1077-1091 (script).
 */
export function HelperStatusDot({
	initial = "checking",
	onStatusChange,
}: HelperStatusDotProps) {
	const [status, setStatus] = useState<HelperStatus>(initial);

	// Real Helper detection: poll the local Helper EXE every 5s. When the
	// Helper answers /info -> "connected"; when it does not (not installed /
	// not running / blocked origin) -> "fallback" (cloud path still works).
	// Click-to-cycle (below) stays available as a manual demo/debug override.
	useEffect(() => {
		let cancelled = false;
		async function poll() {
			while (!cancelled) {
				const up = await helperPing();
				const next: HelperStatus = up ? "connected" : "fallback";
				console.log("[HelperStatusDot] ping", { up, next });
				if (!cancelled) {
					setStatus(next);
					onStatusChange?.(next);
				}
				await new Promise((r) => setTimeout(r, 5000));
			}
		}
		void poll();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function cycle() {
		const idx = CYCLE.indexOf(status as HelperStatus);
		const next = CYCLE[(idx + 1) % CYCLE.length] ?? "connected";
		setStatus(next);
		onStatusChange?.(next);
	}

	const preset = PRESETS[status];
	return (
		<span
			role="button"
			tabIndex={0}
			onClick={cycle}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					cycle();
				}
			}}
			aria-label={`Hybrid Helper ${preset.label}`}
			title={`Hybrid Helper: ${preset.label}`}
			data-status={status}
			data-testid="helper-status-dot"
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 5,
				padding: "3px 10px",
				borderRadius: 999,
				fontSize: 10,
				fontWeight: 700,
				background: preset.bg,
				color: preset.color,
				cursor: "pointer",
				border: "none",
			}}
			{...devLabel("9-G")}
		>
			<span
				style={{
					width: 6,
					height: 6,
					borderRadius: "50%",
					background: "currentColor",
				}}
			/>
			<span>{preset.label}</span>
		</span>
	);
}
